import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

// Components
import { Button, Input, Text } from '@beef/ui-kit';

// Utils
import { validateCellPhone } from 'utils/validators';
import { unMaksPhoneNumber } from 'utils/formatCellNumbers';
import { createInvite } from '../../../../api';

// Constants
import { ERROR_POPUP_TYPES, PHONE_MASK, POPUP_CONSTANTS } from '../../constants';

// Styles
import styles from './styles.pcss';

const cn = classNames.bind(styles);

const InvitePriceTypes = {
  WHOLESALE: 'WHOLESALE',
  BY_NUMBERED: 'BY_NUMBERED',
};

const AddNumberForm = ({
  content, setActivePopup, setErrorType, setFinalCellNumber, invitePriceData, onErrorDescription,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (event) => {
    setErrorMessage('');
    setInputValue(event.target.value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsLoading(true);

    createInvite({ data: { ctn: unMaksPhoneNumber(inputValue) } })
      .then((response) => {
        const data = response.data || {};
        const { error, errorExtra } = data || {};

        if (response.data.isSucceeded) {
          setActivePopup(POPUP_CONSTANTS.invitationSuccessPopup);
          setFinalCellNumber(inputValue);
          return;
        }
        throw error || errorExtra || {};
      })
      .catch((error) => {
        const errorResult = error.errorExtra || error;

        if (Object.keys(errorResult).length) {
          onErrorDescription(errorResult);

          if (errorResult?.errorCode === ERROR_POPUP_TYPES.INVALID_QUERY_PARAM) {
            setErrorMessage(errorResult?.message);
          } else {
            setActivePopup(POPUP_CONSTANTS.errorDescriptionPopup);
          }
          return;
        }

        setErrorType(ERROR_POPUP_TYPES.SMT_GOING_WRONG);
        setActivePopup(POPUP_CONSTANTS.invitationErrorPopup);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isValidForm = useMemo(() => validateCellPhone(inputValue), [inputValue]);

  const isShowPrice = !!invitePriceData;
  const { price, unit, type } = invitePriceData || {};
  const isByNumbered = type === InvitePriceTypes.BY_NUMBERED;
  const {
    title, addNumberFieldLabel, description, btnText, byNumberedPriceTitle, wholesalePriceTitle,
  } = content || {};
  const isPriceTitle = byNumberedPriceTitle || wholesalePriceTitle;

  return (
    <form className={cn('form')} onSubmit={handleSubmit}>
      <div className={cn('form__title')}>
        <Text
          color="grey95"
          size="size2-m"
          tagType="h3"
        >
          {title}
        </Text>
      </div>
      <Input
        placeholder="+7"
        value={inputValue}
        onChange={handleChange}
        label={addNumberFieldLabel}
        name="cellNumber"
        maskSettings={{
          mask: '_',
          format: PHONE_MASK,
        }}
        errorMessage={errorMessage}
      />
      <div className={cn('form__description')}>
        <Text
          color="grey50"
          size="size5-r-s"
        >
          {description}
        </Text>
      </div>
      {isShowPrice && (
        <div className={cn('form__price')}>
          {isPriceTitle && (
            <Text color="grey50" size="size5-r-s">
              {isByNumbered ? byNumberedPriceTitle : wholesalePriceTitle}
            </Text>
          )}
          <Text size="size5-r-s">{`${price} ${unit}`}</Text>
        </div>
      )}
      <div className={cn('form__actions')}>
        <Button
          isLoading={isLoading}
          type="submit"
          variant="secondary"
          size="s"
          disabled={!isValidForm}
        >
          {btnText}
        </Button>
      </div>
    </form>
  );
};

AddNumberForm.propTypes = {
  setActivePopup: PropTypes.func,
  setErrorType: PropTypes.func,
  content: PropTypes.objectOf({
    title: PropTypes.string,
    addNumberFieldLabel: PropTypes.string,
    description: PropTypes.string,
    btnText: PropTypes.string,
    errorMessage: PropTypes.string,
  }),
  setFinalCellNumber: PropTypes.func,
  onErrorDescription: PropTypes.func,
  invitePriceData: PropTypes.objectOf({
    price: PropTypes.number,
    unit: PropTypes.string,
  }),
};

export { AddNumberForm };
